import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MaterialLikeNode} from "#template/nodes/types"
import {MaterialSlotType} from "#template/nodes/types"
import {materialLike} from "#template/nodes/material/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator} from "@cm/material-nodes"
import {ExtractMaterialSlot as ExtractMaterialSlotRuntimeNode} from "#template/runtime-graph/nodes/extract-material-slot"
import {visitNone} from "#template/template-node/visit-helpers"

export type ExtractMaterialSlotType = MaterialSlotType
export const extractMaterialSlotType = z.enum(MaterialSlotType)

export type ExtractMaterialSlotParameters = {
    name: string
    material: MaterialLikeNode | null
    slot: ExtractMaterialSlotType
}

const extractMaterialSlotDefinition: TemplateNodeDefinition<ExtractMaterialSlotParameters> = {
    nodeClass: "ExtractMaterialSlot",
    validation: {
        parameters: z.object({
            name: z.string(),
            material: z.nullable(materialLike),
            slot: extractMaterialSlotType,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ExtractMaterialSlot extends ImageNode<ExtractMaterialSlotParameters> {
    public override get definition() {
        return extractMaterialSlotDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ExtractMaterialSlotParameters> {
        if (this.parameters.material === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const material = evaluator.evaluate(this.parameters.material)
        const [materialValid, materialInvalid] = scope.branch(material)

        const result = scope.node(ExtractMaterialSlotRuntimeNode, {
            sceneManager: evaluator.templateContext.sceneManager,
            material: materialValid,
            slot: this.parameters.slot,
        }).imageGeneratorForSlot
        return scope.phi(result, materialInvalid)
    }
}
